type PrefetchImagesOptions = {
  concurrency?: number;
  signal?: AbortSignal;
  credentials?: RequestCredentials;
  mode?: RequestMode;
  cache?: RequestCache;
  onProgress?: (done: number, total: number) => void;
};

export async function prefetchImages(
  urls: string[],
  {
    concurrency = 6,
    signal,
    credentials = 'include',
    mode = 'no-cors',
    cache = 'default',
    onProgress,
  }: PrefetchImagesOptions = {},
) {
  const unique = Array.from(new Set(urls)).filter(Boolean);
  const total = unique.length;

  let done = 0;
  let index = 0;

  const fetchOne = async (url: string) => {
    const res = await fetch(url, {
      method: 'GET',
      credentials,
      mode,
      cache,
      signal,
    });

    // Optional but helps “complete” the response for caching paths
    await res.arrayBuffer().catch(() => {});

    if (!res.ok) throw new Error(`Failed ${res.status} for ${url}`);
  };

  const nextUrl = () => {
    const i = index;
    index += 1;
    return i < total ? unique[i] : null;
  };

  const runWorker = (): Promise<void> => {
    if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));

    const url = nextUrl();
    if (!url) return Promise.resolve();

    return fetchOne(url)
      .catch(() => {
        // swallow individual failures
      })
      .finally(() => {
        done += 1;
        onProgress?.(done, total);
      })
      .then(runWorker); // recurse
  };

  const workerCount = Math.min(concurrency, total);
  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
}
